import { useAdminContext } from '@/contexts/AdminContext';
import { getUserAvatarUrl, useAuth } from '@/contexts/AuthContext';
import {
    ArrowUpRight,
    Gear, House, SignIn, SignOut
} from '@phosphor-icons/react';
import clsx from 'clsx';
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { NavKey } from '../Settings/SettingsModal';

interface UserMenuProps {
  collapsed: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ collapsed }) => {
    const {
        user, logout, openSignInModal 
    } = useAuth();
    const { modal } = useAdminContext();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // 未登录状态显示 Login 按钮
    if (!user) {
        return (
            <div
                className={clsx(
                    "shrink-0 py-2 border-t border-gray-100/50 z-20",
                    collapsed ? "px-2 flex flex-col items-center" : "px-4"
                )}
            >
                <button
                    onClick={() => openSignInModal()}
                    className="flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors w-full"
                >
                    <SignIn size={18} weight="bold" />
                    <span>Login</span>
                </button>
            </div>
        );
    }

    const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
    const userEmail = user?.email;
    const avatarUrl = getUserAvatarUrl(user);

    return (
        <div
            ref={menuRef}
            className={clsx(
                "shrink-0 py-2 border-t border-gray-100/50 z-20 relative",
                collapsed ? "px-2 flex flex-col items-center" : "px-4"
            )}
        >
            {/* Settings & Logout - Popup Menu */}
            {isOpen ? createPortal(
                <div
                    className="fixed z-[9999] animate-in fade-in zoom-in-95 duration-200"
                    style={{
                        bottom: window.innerHeight - menuRef.current?.getBoundingClientRect().top! + 8,
                        left: menuRef.current?.getBoundingClientRect().left! + 8,
                        width: '14rem'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 space-y-0.5">
                        <button
                            onClick={() => { modal.openModal('profile', { activeTab: NavKey.profile }); setIsOpen(false); }}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
                        >
                            <Gear size={16} weight="bold" className="text-gray-500" />
                            Settings
                        </button>
                        <button
                            onClick={() => { window.open('/?index=1', '_blank'); setIsOpen(false); }}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
                        >
                            <House size={16} weight="bold" className="text-gray-500" />
                            <span className="flex-1">Homepage</span>
                            <ArrowUpRight size={16} weight="bold" className="text-gray-400" />
                        </button>
                        <button
                            onClick={() => { logout() }}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                        >
                            <SignOut size={16} weight="bold" className="shrink-0" />
                            <span className="truncate">Log out</span>
                        </button>
                    </div>
                </div>,
                document.body
            ) : null}

            <div
                className={clsx(
                    "flex items-center gap-3 p-2 rounded-xl cursor-pointer hover:bg-gray-200 transition-colors relative outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
                    isOpen && "bg-gray-200",
                    collapsed && "justify-center"
                )}
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsOpen(!isOpen);
                    }
                }}
            >
                <img
                    src={avatarUrl}
                    alt={userName}
                    className="w-8 h-8 rounded-full border border-gray-200 bg-white"
                />
                {!collapsed && (
                    <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-semibold text-text-main truncate text-gray-900">{userName}</p>
                        <p className="text-xs text-text-sub truncate text-gray-500">{userEmail}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserMenu;
